import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";

/**
 * Mailing-list signup modal — dark theme, instrument-typeface, orange CTA.
 * POSTs to `/api/public/etf-website/subscribe`, served by the Django
 * `etf_website` public router which writes to the Neon `subscribers` table.
 * Form follows the schema's column shapes:
 *   first_name, last_name, email, investor_type ∈ {Individual, Institutional}.
 */
type InvestorType = "Individual" | "Institutional";

type SubscribeResponse = { success?: true; error?: string };

export function MailingListModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [investorType, setInvestorType] = useState<InvestorType>("Individual");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  // Reset on each open so a re-opened modal isn't "stuck" on the success
  // state from a previous submit.
  useEffect(() => {
    if (open) {
      setStatus("idle");
      setErrorMsg(null);
      // autofocus first field
      setTimeout(() => firstFieldRef.current?.focus(), 50);
    }
  }, [open]);

  // Esc to close + body scroll lock
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === "submitting") return;
    setStatus("submitting");
    setErrorMsg(null);
    try {
      const res = await fetch("/api/public/etf-website/subscribe", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          investor_type: investorType,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as SubscribeResponse;
      if (!res.ok || !data.success) {
        setStatus("error");
        setErrorMsg(data.error ?? "Subscription failed. Please try again.");
        return;
      }
      setStatus("success");
      setFirstName("");
      setLastName("");
      setEmail("");
      setInvestorType("Individual");
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please try again.");
    }
  };

  const inputCls =
    "w-full px-[14px] py-3 font-[family-name:var(--font-instrument)] bg-[var(--etf-black3)] border border-[var(--etf-border)] text-[var(--etf-white)] text-sm outline-none transition-[border-color] duration-150 placeholder:text-[var(--etf-white3)] focus:border-[var(--etf-white2)]";
  const labelCls =
    "text-[11px] tracking-[0.16em] uppercase text-[var(--etf-white2)] mb-2 font-[family-name:var(--font-instrument)]";

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click-to-dismiss; keyboard dismissal is handled by the global Escape listener above
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 backdrop-blur-sm px-5"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="mailing-list-title"
    >
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: stops backdrop click from closing the modal; not an interactive control */}
      <div
        ref={dialogRef}
        className="w-full max-w-[480px] bg-[var(--etf-black2)] border border-[var(--etf-border)] shadow-[0_24px_64px_rgba(0,0,0,0.6)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-7 pt-7 pb-2">
          <div>
            <div
              id="mailing-list-title"
              className="font-[family-name:var(--font-f37bolton)] text-[28px] tracking-[-0.02em] leading-none text-[var(--etf-white)]"
            >
              Join the <em className="not-italic text-[var(--etf-orange)]">Corgi</em> Brief
            </div>
            <div className="mt-2 text-sm text-[var(--etf-white3)] font-[family-name:var(--font-instrument)] tracking-[0.02em]">
              Newsletter from the Corgi team delivering fund updates, data points, and top reads
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 -mt-2 text-[var(--etf-white3)] hover:text-[var(--etf-white)] transition-colors p-2 cursor-pointer bg-transparent border-none"
          >
            <svg
              aria-hidden="true"
              focusable="false"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {status === "success" ? (
          <div className="px-7 pt-4 pb-7 font-[family-name:var(--font-instrument)]">
            <div className="text-sm text-[var(--etf-white)] tracking-[0.02em] leading-[1.7]">
              Thanks — you’re on the list. Look for the next Corgi Brief in your inbox.
            </div>
            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-3 bg-[var(--etf-orange)] text-white border-none text-sm tracking-[0.1em] uppercase cursor-pointer transition-opacity duration-150 hover:opacity-[0.88]"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-7 pt-4 pb-7 grid gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="flex flex-col">
                <span className={labelCls}>First name</span>
                <input
                  ref={firstFieldRef}
                  className={inputCls}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  maxLength={100}
                  autoComplete="given-name"
                  required
                />
              </label>
              <label className="flex flex-col">
                <span className={labelCls}>Last name</span>
                <input
                  className={inputCls}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  maxLength={100}
                  autoComplete="family-name"
                  required
                />
              </label>
            </div>

            <label className="flex flex-col">
              <span className={labelCls}>Email address</span>
              <input
                type="email"
                className={inputCls}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={255}
                placeholder="your@email.com"
                autoComplete="email"
                required
              />
            </label>

            <div className="flex flex-col">
              <span className={labelCls}>Investor type</span>
              <div className="grid grid-cols-2 gap-2">
                {(["Individual", "Institutional"] as const).map((opt) => {
                  const active = investorType === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setInvestorType(opt)}
                      className={cn(
                        "px-4 py-3 text-sm font-[family-name:var(--font-instrument)] tracking-[0.04em] cursor-pointer transition-colors duration-150 border",
                        active
                          ? "bg-[var(--etf-black3)] border-[var(--etf-orange)] text-[var(--etf-white)]"
                          : "bg-transparent border-[var(--etf-border)] text-[var(--etf-white2)] hover:text-[var(--etf-white)] hover:border-[var(--etf-white3)]",
                      )}
                      aria-pressed={active}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>

            {errorMsg && (
              <div
                role="alert"
                className="text-xs font-[family-name:var(--font-instrument)] text-[#ff6b6b] tracking-[0.02em]"
              >
                {errorMsg}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 mt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-3 bg-transparent text-[var(--etf-white2)] border border-[var(--etf-border)] text-sm tracking-[0.1em] uppercase cursor-pointer transition-colors duration-150 hover:text-[var(--etf-white)] hover:border-[var(--etf-white3)] font-[family-name:var(--font-instrument)]"
                disabled={status === "submitting"}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={status === "submitting"}
                className="px-5 py-3 bg-[var(--etf-orange)] text-white border-none text-sm tracking-[0.1em] uppercase cursor-pointer transition-opacity duration-150 hover:opacity-[0.88] disabled:opacity-60 disabled:cursor-not-allowed font-[family-name:var(--font-instrument)]"
              >
                {status === "submitting" ? "Subscribing…" : "Subscribe"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
